"use client"

import { addFavorite, delFavorite } from "@/services/spotoverseas"
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"

import { PreciousMetalType } from "../../../../../../services/spotoverseas/interface"
import Style from "../index.module.scss"
import CurrencySwitch, { type CurrencyMode } from "./CurrencySwitch"

interface FavoriteContextValue {
	favoriteMap: Record<string, boolean>
	toggleFavorite: (productId: string) => Promise<void>
}

const FavoriteContext = createContext<FavoriteContextValue | null>(null)

export function useFavoriteContext(): FavoriteContextValue {
	const ctx = useContext(FavoriteContext)
	if (!ctx) throw new Error("useFavoriteContext 必须在 SpotPriceTableShell 内使用")
	return ctx
}

interface SpotPriceTableShellProps {
	title: string
	preciousType?: PreciousMetalType | undefined
	usdContent: ReactNode
	originalContent: ReactNode
	initialFavoriteMap: Record<string, boolean>
}

/**
 * 客户端壳：仅负责货币切换状态与收藏状态，切换时只挂载当前货币的表格（单份 DOM，省内存）
 */
export default function SpotPriceTableShell({
	title,
	preciousType,
	usdContent,
	originalContent,
	initialFavoriteMap,
}: SpotPriceTableShellProps) {
	const [currencyMode, setCurrencyMode] = useState<CurrencyMode>("usd")
	const [favoriteMap, setFavoriteMap] = useState<Record<string, boolean>>(initialFavoriteMap)

	const toggleFavorite = useCallback(
		async (productId: string) => {
			const next = !favoriteMap[productId]
			setFavoriteMap((prev) => ({ ...prev, [productId]: next }))
			try {
				const res = next ? await addFavorite(productId) : await delFavorite(productId)
				if (res?.code !== 0 && res?.code !== undefined) {
					setFavoriteMap((prev) => ({ ...prev, [productId]: !next }))
				}
			} catch {
				setFavoriteMap((prev) => ({ ...prev, [productId]: !next }))
			}
		},
		[favoriteMap]
	)

	const toggleCurrency = useCallback(() => {
		setCurrencyMode((m) => (m === "usd" ? "original" : "usd"))
	}, [])

	const contextValue = useMemo<FavoriteContextValue>(
		() => ({ favoriteMap, toggleFavorite }),
		[favoriteMap, toggleFavorite]
	)

	// 只渲染当前货币对应的表格，避免双份 DOM 占用内存
	const tableContent = currencyMode === "usd" ? usdContent : originalContent

	return (
		<FavoriteContext.Provider value={contextValue}>
			<div className={Style.wrap}>
				<div className={Style.header}>
					<h2 className={Style.title}>Spot {preciousType ? title : ""} Prices</h2>
					<CurrencySwitch mode={currencyMode} onToggle={toggleCurrency} />
				</div>
				{tableContent}
			</div>
		</FavoriteContext.Provider>
	)
}
